import React from 'react';
import { Link } from 'react-router-dom';
import pageStyles from './MarketingPage.module.css';
import styles from './ProductPage.module.css';
import CTAGroup from '../components/marketing/CTAGroup';
import FinalCTASection from '../components/marketing/sections/FinalCTASection';
import StatusTag from '../components/marketing/StatusTag';
import useDocumentMeta from '../hooks/useDocumentMeta';
import useHashScroll from '../hooks/useHashScroll';
import useReveal from '../hooks/useReveal';

const AREAS = [
  { href: '#contacts', label: 'Contacts' },
  { href: '#intelligence', label: 'Intelligence' },
  { href: '#events', label: 'Networking' },
  { href: '#analytics', label: 'Analytics' },
  { href: '#teams', label: 'Teams' },
];

const COMING = [
  {
    title: 'Automated AI follow-ups',
    body: 'A draft from your notes, and a prompt while the meeting is still worth reviving.',
  },
  {
    title: 'Role-based seats',
    body: 'The account stays with the position, so relationships do not leave with a person.',
  },
  {
    title: 'Event outcome reporting',
    body: 'Which occasions produced contacts and engagement, so next year\'s diary is based on evidence.',
  },
  {
    title: 'Network attendance',
    body: 'See who else from your network is going, before you commit to the room.',
  },
];

const PreviewShell = ({ label, children, note }) => (
  <figure className={styles.preview}>
    <div className={styles.previewBar}>
      <span className={styles.previewDot} />
      {label}
    </div>
    <div className={styles.previewBody}>{children}</div>
    {note ? <figcaption className={styles.previewNote}>{note}</figcaption> : null}
  </figure>
);

const ProductPage = () => {
  const revealRef = useReveal();
  useHashScroll();

  useDocumentMeta({
    title: 'The Velox platform',
    description:
      'Velox captures the people you meet, keeps their details live, and helps you remember, find and follow up. Contacts, intelligence, networking and analytics in one system.',
    path: '/product',
  });

  return (
    <div ref={revealRef}>
      <section className={pageStyles.pageHero}>
        <div className="vxm-grid-motif" />
        <div className={`vxm-container vxm-container--narrow ${pageStyles.pageHeroInner}`}>
          <p className="vxm-eyebrow">Product</p>
          <h1 className="vxm-h1">The system behind the introduction</h1>
          <p className="vxm-lead">
            A contactless card is one way to share in the room. Velox is what
            keeps the person, the context, and the next conversation.
          </p>
          <CTAGroup source="product_hero" />
          <ul className={styles.jump}>
            {AREAS.map((area) => (
              <li key={area.href}>
                <a href={area.href}>{area.label}</a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className={`vxm-section ${styles.capability}`} id="contacts">
        <div className="vxm-container">
          <div className={`${styles.split} vxm-reveal`}>
            <div className={styles.copy}>
              <p className="vxm-eyebrow">Contacts</p>
              <h2 className="vxm-h2">A relationship record that stays current</h2>
              <p className={`vxm-body ${styles.copyLead}`}>
                Most introductions die in a list. Velox keeps the live details,
                the room you met in, and the note you wrote while it was still
                clear.
              </p>
              <ul className={styles.points}>
                <li>When they change role or number, your copy of their contact changes too.</li>
                <li>Place, date and private notes stay with the person. Notes are never shown to administrators.</li>
                <li>People without a Velox account can still send their details back to you.</li>
                <li>Photograph paper cards into the same list, and invite those people to connect.</li>
              </ul>
              <Link to="/how-it-works" className="vxm-link">
                See how capture works
              </Link>
            </div>
            <PreviewShell
              label="Contacts"
              note="Illustrative layout. Your list shows the people you have actually met."
            >
              <div className={styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.rowTitle}>Name and company, always current</span>
                  <span className={styles.rowMeta}>Where you met · the date · a private note</span>
                </div>
                <span className={styles.pill}>Live</span>
              </div>
              <div className={styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.rowTitle}>Guest capture</span>
                  <span className={styles.rowMeta}>No account required on their side</span>
                </div>
              </div>
              <div className={styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.rowTitle}>Paper cards, in the same place</span>
                  <span className={styles.rowMeta}>Scan, then invite to connect</span>
                </div>
              </div>
            </PreviewShell>
          </div>
        </div>
      </section>

      <section className={`vxm-section vxm-section--raised ${styles.capability}`} id="intelligence">
        <div className="vxm-container">
          <div className={`${styles.split} ${styles.splitReverse} vxm-reveal`}>
            <div className={styles.copy}>
              <p className="vxm-eyebrow">Market Intelligence</p>
              <h2 className="vxm-h2">Stay briefed on the market you work in</h2>
              <p className={`vxm-body ${styles.copyLead}`}>
                Velox writes a weekly digest of what moved in your industry, and
                a daily brief on the companies and topics you asked it to watch.
              </p>
              <ul className={styles.points}>
                <li>The weekly digest arrives by email, written for the industries you set.</li>
                <li>The daily brief is in the product, so you do not have to go looking.</li>
                <li>Preferences live on your account: industries, topics, companies, location.</li>
              </ul>
            </div>
            <PreviewShell
              label="Intelligence"
              note="The digest is generated from your preferences, not a generic news feed."
            >
              <div className={styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.rowTitle}>Weekly market digest</span>
                  <span className={styles.rowMeta}>Emailed · written for your industry</span>
                </div>
              </div>
              <div className={styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.rowTitle}>Daily brief</span>
                  <span className={styles.rowMeta}>In the product each morning</span>
                </div>
              </div>
              <div className={styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.rowTitle}>Watchlist</span>
                  <span className={styles.rowMeta}>Companies, topics and locations you chose</span>
                </div>
              </div>
            </PreviewShell>
          </div>
        </div>
      </section>

      <section className={`vxm-section ${styles.capability}`} id="events">
        <div className="vxm-container">
          <div className={`${styles.split} vxm-reveal`}>
            <div className={styles.copy}>
              <p className="vxm-eyebrow">Networking Opportunities</p>
              <h2 className="vxm-h2">Rooms that match how you work</h2>
              <p className={`vxm-body ${styles.copyLead}`}>
                Conferences, fairs and meet-ups are suggested from your industry
                and location, so the diary fills with occasions that are likely
                to produce introductions.
              </p>
              <ul className={styles.points}>
                <li>Discovery is matched to the industries and location on your account.</li>
                <li>Seeing who else from your network is attending is in development, and labelled as such wherever it appears.</li>
              </ul>
            </div>
            <PreviewShell
              label="Networking"
              note="Recommendations come from your industry and location, not from a public directory dump."
            >
              <div className={styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.rowTitle}>Industry-matched events</span>
                  <span className={styles.rowMeta}>Filtered to your preferences</span>
                </div>
                <span className={styles.pill}>Matched</span>
              </div>
              <div className={styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.rowTitle}>Location-aware</span>
                  <span className={styles.rowMeta}>Near you, or worth travelling for</span>
                </div>
              </div>
            </PreviewShell>
          </div>
        </div>
      </section>

      <section className={`vxm-section vxm-section--raised ${styles.capability}`} id="analytics">
        <div className="vxm-container">
          <div className={`${styles.split} ${styles.splitReverse} vxm-reveal`}>
            <div className={styles.copy}>
              <p className="vxm-eyebrow">Analytics</p>
              <h2 className="vxm-h2">See what the meeting actually produced</h2>
              <p className={`vxm-body ${styles.copyLead}`}>
                Profile views, link clicks and card taps over time, so you can
                tell a productive room from a busy one. For teams, the same
                picture compares people and groups.
              </p>
              <ul className={styles.points}>
                <li>Individual analytics cover views, clicks and taps, not revenue.</li>
                <li>Company accounts add leaderboards and team-by-team comparison.</li>
                <li>Event ROI and booked-meeting outcomes are still in development.</li>
              </ul>
              <Link to="/professionals" className="vxm-link">
                Velox for professionals
              </Link>
            </div>
            <PreviewShell
              label="Analytics"
              note="Meters illustrate the categories that exist today. They are not a live account."
            >
              <div className={styles.meters}>
                <div className={styles.meter}>
                  <span className={styles.meterLabel}>Profile views</span>
                  <span className={styles.meterTrack}><span className={styles.meterFill} style={{ width: '78%' }} /></span>
                </div>
                <div className={styles.meter}>
                  <span className={styles.meterLabel}>Link clicks</span>
                  <span className={styles.meterTrack}><span className={styles.meterFill} style={{ width: '46%' }} /></span>
                </div>
                <div className={styles.meter}>
                  <span className={styles.meterLabel}>Card taps</span>
                  <span className={styles.meterTrack}><span className={styles.meterFill} style={{ width: '62%' }} /></span>
                </div>
              </div>
              <div className={styles.row}>
                <div className={styles.rowMain}>
                  <span className={styles.rowTitle}>Team comparison</span>
                  <span className={styles.rowMeta}>On a company account: people and teams, side by side</span>
                </div>
              </div>
            </PreviewShell>
          </div>
        </div>
      </section>

      <section className={`vxm-section ${styles.capability}`} id="teams">
        <div className={`vxm-container vxm-container--narrow`}>
          <div className={`${pageStyles.panel} vxm-reveal`}>
            <p className="vxm-eyebrow">Teams</p>
            <h2 className={pageStyles.panelTitle}>The network belongs to the business</h2>
            <p className="vxm-body" style={{ marginBottom: 'var(--vxm-space-5)' }}>
              A shared contact hub, seats for your people, analytics across the
              organisation, and optional customised metal cards. Private notes
              stay private. Role-based seats that survive a leaver are still
              being built, and are labelled wherever they appear.
            </p>
            <Link to="/enterprise" className="vxm-btn vxm-btn--secondary">
              Velox for teams
            </Link>
          </div>
        </div>
      </section>

      <section className="vxm-section vxm-section--raised" id="coming">
        <div className="vxm-container vxm-container--narrow">
          <div className="vxm-section-head vxm-reveal">
            <p className="vxm-eyebrow">Coming</p>
            <h2 className="vxm-h2">Committed, and not mixed in with today</h2>
            <p className="vxm-body">
              These are on the roadmap. They are not available in the product
              yet.
            </p>
          </div>
          <dl className={`${styles.comingGrid} vxm-reveal`}>
            {COMING.map((item) => (
              <div key={item.title} className={styles.comingItem}>
                <dt>
                  {item.title}
                  <StatusTag>In development</StatusTag>
                </dt>
                <dd>{item.body}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <FinalCTASection
        title="Start with a profile"
        body="Create your profile today. Add a card when you want one, or talk to us about Velox for your team."
        source="product_final"
      />
    </div>
  );
};

export default ProductPage;
